/**
 * Source Deletion Controller and UX State Governance (AGY_PERSONAL_FINAL_UX).
 *
 * Implements:
 * - Deterministic canDeleteSource predicate (disabled when no active doc or no source)
 * - Canonical Turkish confirmation message warning of irreversible source loss while vector editing/export remains
 * - Atomic execution of source deletion via typed AuthLibrary/API
 * - State synchronization marking CIM source_deleted=true and clearing asset IDs
 * - Invariant preservation: vector objects remain 100% intact, interactive, and exportable
 */

import type { CIMDocument } from "./model.ts";

export const SOURCE_DELETE_CONFIRMATION_MSG =
  "Kaynak fotoğrafı silmek istediğinize emin misiniz?\n\nBu işlem geri alınamaz. Kaynak karşılaştırması kullanılamaz hale gelecektir ancak vektör çizimleriniz düzenlenebilir ve dışa aktarılabilir kalacaktır.";

export interface SourceDeletionAuthClient {
  deleteSource(documentId: string): Promise<void | unknown>;
}

export interface SourceDeletionContext {
  documentId: string;
  cimDocument: CIMDocument;
  authLibrary: SourceDeletionAuthClient;
  confirmFn?: () => boolean;
}

export interface SourceDeletionResult {
  confirmed: boolean;
  deleted: boolean;
  error?: string;
}

/**
 * Predicate to determine whether the user-facing source deletion button should be enabled.
 * Disabled when:
 * - No active cloud/personal document ID is present.
 * - No CIM document is present.
 * - Document source is already marked deleted.
 * - Rendered source is absent or dimensions are non-positive.
 */
export function canDeleteSource(
  documentId: string | null | undefined,
  cimDocument: CIMDocument | null | undefined,
  hasRenderedSource: boolean,
  renderedWidth: number,
  renderedHeight: number
): boolean {
  if (!documentId) return false;
  if (!cimDocument) return false;
  if (cimDocument.source?.source_deleted) return false;
  if (!hasRenderedSource) return false;
  if (renderedWidth <= 0 || renderedHeight <= 0) return false;
  return true;
}

/**
 * Execute user-facing source deletion workflow.
 * Prompts user for explicit confirmation, calls typed auth/document client,
 * and synchronizes the active CIMDocument state.
 */
export async function executeSourceDeletion(
  ctx: SourceDeletionContext
): Promise<SourceDeletionResult> {
  const confirm = ctx.confirmFn ?? (() => {
    if (typeof window !== "undefined" && typeof window.confirm === "function") {
      return window.confirm(SOURCE_DELETE_CONFIRMATION_MSG);
    }
    return false;
  });

  const isConfirmed = confirm();
  if (!isConfirmed) {
    return { confirmed: false, deleted: false };
  }

  try {
    await ctx.authLibrary.deleteSource(ctx.documentId);

    // Update active CIM document state consistently
    if (ctx.cimDocument && ctx.cimDocument.source) {
      ctx.cimDocument.source.source_deleted = true;
      ctx.cimDocument.source.original_asset_id = null;
      ctx.cimDocument.source.prepared_asset_id = null;
      ctx.cimDocument.source.normalized_asset_id = null;
    }

    return { confirmed: true, deleted: true };
  } catch (err: any) {
    return {
      confirmed: true,
      deleted: false,
      error: err.message || String(err),
    };
  }
}
